import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaTag,
  FaPlus,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaGlobe,
  FaSave,
  FaCircle,
  FaCrown, // Nuevo icono para admin
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';
import styles from '../CSS/EtiquetasModal.module.css';

const EtiquetasModal = ({ 
  user, 
  departamentos, 
  etiquetas, 
  adminsDepartamento,
  onClose, 
  onUpdateEtiquetas 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Formulario simplificado (sin descripción)
  const [formData, setFormData] = useState({
    nombre: '',
    color: '#3498db',
    departamento_id: '', // '' = general, 'id' = específico del departamento
    activa: true
  });

  // MODIFICADO: Detectar si es admin general
  const [isGeneralAdmin, setIsGeneralAdmin] = useState(false);

  useEffect(() => {
    // Función para verificar si el usuario es admin general
    const checkIfGeneralAdmin = async () => {
      try {
        // Buscar el usuario en la colección 'users' de Firebase
        const userDoc = await getDocs(query(
          collection(db, 'users'),
          where('__name__', '==', user.email) // Asumiendo que el email es el ID del documento
        ));

        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          setIsGeneralAdmin(userData.role === 'admin');
        } else {
          // Si no se encuentra en 'users', asumir que no es admin general
          setIsGeneralAdmin(false);
        }
      } catch (error) {
        console.error('Error al verificar rol de admin:', error);
        setIsGeneralAdmin(false);
      }
    };

    checkIfGeneralAdmin();
  }, [user.email]);

  // MODIFICADO: Determinar si es admin de departamento por email
  const adminDepartamento = adminsDepartamento.find(admin => admin.email === user.email);
  const departamentoAdmin = adminDepartamento ? 
    departamentos.find(dept => dept.id === adminDepartamento.departamento_id) : null;
  
  // MODIFICADO: Determinar el rol del usuario con prioridad para admin general
  const userRole = isGeneralAdmin ? 'admin_general' : 
                   adminDepartamento ? 'admin_departamento' : 'user';

  // Colores predefinidos para etiquetas
  const coloresPredefinidos = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12',
    '#9b59b6', '#1abc9c', '#34495e', '#e67e22',
    '#8e44ad', '#27ae60', '#2980b9', '#c0392b',
    '#16a085', '#d35400', '#7f8c8d', '#f1c40f'
  ];

  // MODIFICADO: Filtrar etiquetas según el rol del usuario
  const etiquetasFiltradas = etiquetas.filter(etiqueta => {
    if (userRole === 'admin_general') {
      return true; // Admin general puede ver todas
    } else if (userRole === 'admin_departamento') {
      // Admin departamento solo ve las generales y las de su departamento
      return !etiqueta.departamento_id || 
             etiqueta.departamento_id === adminDepartamento?.departamento_id;
    }
    return false; // Usuarios normales no ven nada (puedes cambiar esto según tu lógica)
  });

  // Cálculos de paginación
  const totalItems = etiquetasFiltradas.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEtiquetas = etiquetasFiltradas.slice(startIndex, endIndex);

  // Resetear página cuando cambien las etiquetas
  useEffect(() => {
    setCurrentPage(1);
  }, [etiquetasFiltradas.length]);

  useEffect(() => {
    // Limpiar mensajes después de 3 segundos
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // NUEVO: useEffect para resetear el formulario cuando cambien los datos del usuario
  useEffect(() => {
    // Resetear el formulario cuando cambie el rol del usuario o departamento
    if (userRole && (userRole !== 'user')) {
      resetForm();
    }
  }, [userRole, adminDepartamento?.departamento_id]);

  // Funciones de navegación de paginación
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // MODIFICADO: Función resetForm mejorada
  const resetForm = () => {
    const defaultDepartamentoId = userRole === 'admin_departamento' && adminDepartamento
      ? adminDepartamento.departamento_id 
      : '';
      
    setFormData({
      nombre: '',
      color: '#3498db',
      departamento_id: defaultDepartamentoId,
      activa: true
    });
    setIsCreating(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.nombre.trim()) {
        throw new Error('El nombre de la etiqueta es obligatorio');
      }

      // Validar longitud del nombre
      if (formData.nombre.trim().length > 30) {
        throw new Error('El nombre de la etiqueta no puede exceder 30 caracteres');
      }

      // Verificar que el nombre no exista ya
      const nombreExistente = etiquetas.find(
        etiqueta => 
          etiqueta.nombre.toLowerCase() === formData.nombre.toLowerCase() &&
          etiqueta.departamento_id === formData.departamento_id &&
          etiqueta.id !== editingId
      );

      if (nombreExistente) {
        throw new Error('Ya existe una etiqueta con ese nombre en este ámbito');
      }

      const etiquetaData = {
        ...formData,
        nombre: formData.nombre.trim(),
        updated_at: Timestamp.now(),
        updated_by: user.uid
      };

      if (editingId) {
        // Actualizar etiqueta existente
        await updateDoc(doc(db, 'etiquetas', editingId), etiquetaData);
        setSuccessMessage('Etiqueta actualizada exitosamente');
      } else {
        // Crear nueva etiqueta
        etiquetaData.created_at = Timestamp.now();
        etiquetaData.created_by = user.uid;
        
        await addDoc(collection(db, 'etiquetas'), etiquetaData);
        setSuccessMessage('Etiqueta creada exitosamente');
      }

      // Recargar etiquetas
      await reloadEtiquetas();
      resetForm();

    } catch (error) {
      console.error('Error al guardar etiqueta:', error);
      setError(error.message || 'Error al guardar la etiqueta');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (etiqueta) => {
    setFormData({
      nombre: etiqueta.nombre,
      color: etiqueta.color,
      departamento_id: etiqueta.departamento_id || '',
      activa: etiqueta.activa !== false
    });
    setEditingId(etiqueta.id);
    setIsCreating(true);
  };

  const handleDelete = async (etiquetaId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta etiqueta?')) {
      return;
    }

    setLoading(true);
    try {
      // Verificar si la etiqueta está siendo usada en tickets
      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('etiquetas', 'array-contains', etiquetaId)
      );
      const ticketsSnapshot = await getDocs(ticketsQuery);

      if (!ticketsSnapshot.empty) {
        throw new Error('No se puede eliminar la etiqueta porque está siendo usada en tickets');
      }

      await deleteDoc(doc(db, 'etiquetas', etiquetaId));
      setSuccessMessage('Etiqueta eliminada exitosamente');
      
      // Recargar etiquetas
      await reloadEtiquetas();

    } catch (error) {
      console.error('Error al eliminar etiqueta:', error);
      setError(error.message || 'Error al eliminar la etiqueta');
    } finally {
      setLoading(false);
    }
  };

  const reloadEtiquetas = async () => {
    try {
      const etiquetasQuery = query(collection(db, 'etiquetas'));
      const etiquetasSnapshot = await getDocs(etiquetasQuery);
      const nuevasEtiquetas = etiquetasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      onUpdateEtiquetas(nuevasEtiquetas);
    } catch (error) {
      console.error('Error al recargar etiquetas:', error);
    }
  };

  // MODIFICADO: Función para verificar permisos de edición/eliminación
  const canEditDelete = (etiqueta) => {
    if (userRole === 'admin_general') return true; // Admin general puede editar/eliminar todas
    if (userRole === 'admin_departamento') {
      // Solo puede editar/eliminar las de su departamento
      return etiqueta.departamento_id === adminDepartamento?.departamento_id;
    }
    return false;
  };

  const getDepartamentoNombre = (departamentoId) => {
    if (!departamentoId) return 'General';
    const dept = departamentos.find(d => d.id === departamentoId);
    return dept?.nombre || 'Desconocido';
  };

  // MODIFICADO: Función para obtener departamentos disponibles según el rol
  const getDepartamentosDisponibles = () => {
    if (userRole === 'admin_general') {
      return departamentos; // Admin general ve todos
    } else if (userRole === 'admin_departamento') {
      return departamentoAdmin ? [departamentoAdmin] : []; // Admin departamento solo ve el suyo
    }
    return [];
  };

  const departamentosDisponibles = getDepartamentosDisponibles();

  // MODIFICADO: Función para manejar el click de Nueva Etiqueta
  const handleCreateClick = () => {
    // Asegurar que el formulario tenga los valores correctos antes de mostrar
    const defaultDepartamentoId = userRole === 'admin_departamento' && adminDepartamento
      ? adminDepartamento.departamento_id 
      : '';
      
    setFormData({
      nombre: '',
      color: '#3498db',
      departamento_id: defaultDepartamentoId,
      activa: true
    });
    setIsCreating(true);
  };

  return (
    <motion.div 
      className={styles.modalOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className={styles.modalContent}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className={styles.modalHeader}>
          <div className={styles.titleContainer}>
            <h2 className={styles.modalTitle}>
              <FaTag className={styles.modalIcon} />
              Gestión de Etiquetas
            </h2>
            {/* MODIFICADO: Badges en contenedor separado para móvil */}
            <div className={styles.badgeContainer}>
              {userRole === 'admin_general' && (
                <span className={styles.adminGeneralBadge}>
                  <FaCrown className={styles.crownIcon} />
                  Administrador General
                </span>
              )}
              {userRole === 'admin_departamento' && departamentoAdmin && (
                <span className={styles.departmentBadge}>
                  {departamentoAdmin.nombre}
                </span>
              )}
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Mensajes */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {successMessage && (
            <div className={styles.successMessage}>
              {successMessage}
            </div>
          )}

          {/* Botón crear nueva etiqueta */}
          {!isCreating && (
            <div className={styles.createSection}>
              <button 
                className={styles.createButton}
                onClick={handleCreateClick}
              >
                <FaPlus className={styles.btnIcon} />
                Nueva Etiqueta
              </button>
            </div>
          )}

          {/* Formulario de crear/editar - MODIFICADO */}
          {isCreating && (
            <motion.div 
              className={styles.formSection}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <h3>{editingId ? 'Editar Etiqueta' : 'Nueva Etiqueta'}</h3>
              
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Nombre *
                    </label>
                    <input
                      type="text"
                      className={styles.input}
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      placeholder="Ej: Urgente, Bug, Mejora..."
                      maxLength={30}
                      required
                    />
                    <small className={styles.charCounter}>
                      {formData.nombre.length}/30 caracteres
                    </small>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Color
                    </label>
                    <div className={styles.colorContainer}>
                      <input
                        type="color"
                        className={styles.colorInput}
                        value={formData.color}
                        onChange={(e) => setFormData({...formData, color: e.target.value})}
                      />
                      <div className={styles.colorPreview}>
                        <div className={styles.colorPredefinidos}>
                          {coloresPredefinidos.map(color => (
                            <button
                              key={color}
                              type="button"
                              className={`${styles.colorOption} ${formData.color === color ? styles.colorSelected : ''}`}
                              style={{ backgroundColor: color }}
                              onClick={() => setFormData({...formData, color})}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MODIFICADO: Select de ámbito mejorado */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Ámbito
                    {userRole === 'admin_general' && (
                      <span className={styles.labelHelper}>
                        (Como admin general puedes crear etiquetas para cualquier departamento)
                      </span>
                    )}
                  </label>
                  <select
                    className={styles.select}
                    value={formData.departamento_id}
                    onChange={(e) => setFormData({...formData, departamento_id: e.target.value})}
                    disabled={userRole === 'admin_departamento'} // Solo admin departamento está restringido
                  >
                    {/* Admin general puede elegir general o cualquier departamento */}
                    {userRole === 'admin_general' && (
                      <option value="">General</option>
                    )}
                    
                    {/* Mostrar departamentos disponibles según el rol */}
                    {departamentosDisponibles.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.nombre}
                      </option>
                    ))}
                  </select>
                  
                  {userRole === 'admin_departamento' && (
                    <small className={styles.helpText}>
                      Solo puedes crear etiquetas para tu departamento
                    </small>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button 
                    type="button" 
                    className={styles.cancelButton}
                    onClick={resetForm}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className={styles.saveButton}
                    disabled={loading}
                  >
                    <FaSave className={styles.btnIcon} />
                    {loading ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Lista de etiquetas con paginación */}
          <div className={styles.etiquetasList}>
            <div className={styles.etiquetasHeader}>
              <h3>Etiquetas Existentes ({totalItems})</h3>
              
              {/* Info de paginación */}
              {totalPages > 1 && (
                <div className={styles.pageInfo}>
                  Mostrando {startIndex + 1}-{Math.min(endIndex, totalItems)} de {totalItems}
                </div>
              )}
            </div>
            
            {totalItems === 0 ? (
              <div className={styles.emptyState}>
                <FaTag className={styles.emptyIcon} />
                <p>No hay etiquetas disponibles</p>
                <p className={styles.emptySubtext}>Crea la primera etiqueta para comenzar</p>
              </div>
            ) : (
              <>
                <div className={styles.etiquetasGrid}>
                  {currentEtiquetas.map(etiqueta => (
                    <div key={etiqueta.id} className={styles.etiquetaCard}>
                      <div className={styles.etiquetaHeader}>
                        <div className={styles.etiquetaInfo}>
                          <div 
                            className={styles.etiquetaBadge}
                            style={{ backgroundColor: etiqueta.color }}
                          >
                            <FaTag className={styles.etiquetaIcon} />
                            {etiqueta.nombre}
                          </div>
                          <div className={styles.etiquetaAmbito}>
                            {etiqueta.departamento_id ? (
                              <>
                                <FaBuilding className={styles.ambitoIcon} />
                                {getDepartamentoNombre(etiqueta.departamento_id)}
                              </>
                            ) : (
                              <>
                                <FaGlobe className={styles.ambitoIcon} />
                                General
                              </>
                            )}
                          </div>
                        </div>

                        {canEditDelete(etiqueta) && (
                          <div className={styles.etiquetaActions}>
                            <button
                              className={styles.editButton}
                              onClick={() => handleEdit(etiqueta)}
                              title="Editar etiqueta"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDelete(etiqueta.id)}
                              title="Eliminar etiqueta"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Controles de paginación */}
                {totalPages > 1 && (
                  <div className={styles.paginationContainer}>
                    <div className={styles.paginationControls}>
                      <button
                        className={`${styles.paginationButton} ${styles.paginationNavButton}`}
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        title="Página anterior"
                      >
                        <FaChevronLeft size={12} />
                      </button>

                      <span className={styles.pageIndicator}>
                        Página {currentPage} de {totalPages}
                      </span>

                      <button
                        className={`${styles.paginationButton} ${styles.paginationNavButton}`}
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        title="Página siguiente"
                      >
                        <FaChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EtiquetasModal;